import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { request } from "app/network";
import Swal from "sweetalert2";

interface ReorderVideoModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    videos: Array<{ seqId: number; title: string; displayOrder: number; thumbnailUrl?: string }>;
    darkMode?: boolean;
}

interface SortableVideoItemProps {
    id: string;
    title: string;
    thumbnailUrl?: string;
    darkMode: boolean;
}

function SortableVideoItem({ id, title, thumbnailUrl, darkMode }: SortableVideoItemProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <MDBox
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                border: darkMode
                    ? "1px solid rgba(255, 255, 255, 0.2)"
                    : "1px solid rgba(0, 0, 0, 0.2)",
                backgroundColor: darkMode
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.02)",
                cursor: "grab",
                "&:active": {
                    cursor: "grabbing",
                },
            }}
        >
            <DragIndicatorIcon
                sx={{
                    color: darkMode ? "rgba(255, 255, 255, 0.8)" : "rgba(0, 0, 0, 0.8)",
                }}
            />
            {thumbnailUrl && (
                <MDBox
                    component="img"
                    src={thumbnailUrl}
                    alt={title}
                    sx={{
                        width: 60,
                        height: 45,
                        objectFit: "cover",
                        borderRadius: "4px",
                        flexShrink: 0,
                    }}
                />
            )}
            <MDTypography
                variant="button"
                fontWeight="regular"
                sx={{
                    color: darkMode
                        ? "rgba(255, 255, 255, 0.8)"
                        : "#344767",
                    flex: 1,
                }}
            >
                {title}
            </MDTypography>
        </MDBox>
    );
}

function ReorderVideoModal({ open, onClose, onSuccess, videos, darkMode = false }: ReorderVideoModalProps) {
    const [orderedVideos, setOrderedVideos] = useState(
        videos.sort((a, b) => a.displayOrder - b.displayOrder)
    );
    const [loading, setLoading] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setOrderedVideos((items) => {
                const oldIndex = items.findIndex((item) => `video-${item.seqId}` === active.id);
                const newIndex = items.findIndex((item) => `video-${item.seqId}` === over.id);

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            // 각 영상의 displayOrder를 업데이트
            const updates = orderedVideos.map((video, index) => ({
                seqId: video.seqId,
                displayOrder: index + 1,
            }));

            // 순차적으로 업데이트 (병렬 처리 시 순서 보장 안됨)
            for (const update of updates) {
                await request("ExerciseVideo.updateDisplayOrder", {
                    paramz: {
                        SeqId: update.seqId,
                        DisplayOrder: update.displayOrder,
                    },
                });
            }

            Swal.fire({
                title: "성공",
                text: "영상 순서가 변경되었습니다.",
                icon: "success",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });

            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("영상 순서 변경 실패:", error);
            Swal.fire({
                title: "오류",
                text: error.message || "영상 순서 변경에 실패했습니다.",
                icon: "error",
                confirmButtonText: "확인",
                background: darkMode ? "#1a2035" : "#fff",
                color: darkMode ? "#fff" : "#000",
            });
        } finally {
            setLoading(false);
        }
    };

    // Reset ordered videos when modal opens
    React.useEffect(() => {
        if (open) {
            setOrderedVideos(videos.sort((a, b) => a.displayOrder - b.displayOrder));
        }
    }, [open, videos]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: darkMode ? "#1a2035" : "#fff",
                    color: darkMode ? "#fff" : "#000",
                },
            }}
        >
            <DialogTitle>영상 순서 변경</DialogTitle>
            <DialogContent>
                <MDBox pt={2}>
                    <MDTypography
                        variant="caption"
                        fontWeight="regular"
                        sx={{ color: darkMode ? "rgba(255, 255, 255, 0.8)" : "#344767" }}
                        mb={2}
                        display="block"
                    >
                        드래그하여 영상 순서를 변경하세요
                    </MDTypography>
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={orderedVideos.map((video) => `video-${video.seqId}`)}
                            strategy={verticalListSortingStrategy}
                        >
                            {orderedVideos.map((video) => (
                                <SortableVideoItem
                                    key={video.seqId}
                                    id={`video-${video.seqId}`}
                                    title={video.title}
                                    thumbnailUrl={video.thumbnailUrl}
                                    darkMode={darkMode}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </MDBox>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="outlined" color="secondary">
                    취소
                </MDButton>
                <MDButton
                    onClick={handleSave}
                    variant="gradient"
                    color="info"
                    disabled={loading}
                >
                    {loading ? "저장 중..." : "저장"}
                </MDButton>
            </DialogActions>
        </Dialog>
    );
}

export default ReorderVideoModal;
